import { useEffect, useState } from "react";
import { supabase } from '../services/supabase';
import { mintNFT } from '../services/web3';

const PAGE_SIZE = 24;

export default function Gallery() {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [mintingId, setMintingId] = useState(null);
  const [error, setError] = useState(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => { loadNFTs(currentPage); }, [currentPage]);

  async function loadNFTs(page) {
    setLoading(true);
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const { data, error, count } = await supabase
      .from("nfts")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);
    if (error) { console.error(error); setLoading(false); return; }
    setNfts(data || []);
    setTotalCount(count || 0);
    setLoading(false);
  }

  async function handleMint(nft) {
    setError(null);
    setMintingId(nft.id);
    try {
      const txHash = await mintNFT(nft.token_id, nft.metadata_url, nft.price_eth);
      await supabase
        .from('nfts')
        .update({ minted: true, owner_address: txHash })
        .eq('id', nft.id);
      await loadNFTs(currentPage);
    } catch (err) {
      console.error(err);
      setError(err.message || "Minting failed. Please try again.");
    } finally {
      setMintingId(null);
    }
  }

  function handlePrev() {
    if (currentPage > 1) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setCurrentPage((p) => p - 1);
    }
  }

  function handleNext() {
    if (currentPage < totalPages) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setCurrentPage((p) => p + 1);
    }
  }

  function getPageNumbers() {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push("..."); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push("..."); pages.push(totalPages); }
    return pages;
  }

  if (loading) return <div className="container py-5 text-center text-muted">Loading gallery...</div>;
  if (!nfts.length) return <div className="container py-5 text-center text-muted">No NFTs available.</div>;

  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);

  return (
    <div className="container py-4">
      <h2 className="mb-4">NFT Gallery</h2>
      <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="me-2 flex-shrink-0" viewBox="0 0 16 16">
    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
  </svg>
  <div>
    To mint an NFT you will need the <strong>MetaMask browser extension</strong> installed. 
    Don't have it? <a href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer">Download MetaMask here</a>.
  </div>
</div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} />
        </div>
      )}

      <p className="text-muted small mb-3">
        Showing {startItem}–{endItem} of {totalCount} items
      </p>

      <div className="row g-4">
        {nfts.map((nft) => (
          <div className="col-md-4 col-lg-3" key={nft.id}>
            <div className="card h-100 shadow-sm">
              <img
                src={nft.image_url}
                className="card-img-top"
                style={{ height: "220px", objectFit: "cover" }}
                alt={nft.title}
              />
              <div className="card-body">
                <h5 className="card-title">{nft.title}</h5>
                <p className="card-text small text-muted">{nft.description}</p>
              </div>
              <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center">
                <strong>{nft.price_eth} ETH</strong>
                {nft.minted ? (
                  <span className="badge bg-secondary">Sold</span>
                ) : (
                  <button
                    className="btn btn-sm btn-dark"
                    onClick={() => handleMint(nft)}
                    disabled={mintingId === nft.id}
                  >
                    {mintingId === nft.id ? "Minting..." : "Mint"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <nav className="mt-5 d-flex justify-content-center align-items-center gap-2">
          <button className="btn btn-outline-secondary" onClick={handlePrev} disabled={currentPage === 1}>Prev</button>
          {getPageNumbers().map((page, idx) =>
            page === "..." ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-muted">…</span>
            ) : (
              <button
                key={page}
                className={`btn ${page === currentPage ? "btn-dark" : "btn-outline-secondary"}`}
                style={{ minWidth: "40px" }}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            )
          )}
          <button className="btn btn-outline-secondary" onClick={handleNext} disabled={currentPage === totalPages}>Next</button>
        </nav>
      )}
    </div>
  );
}
